// Links from an address to its monitoring: the ERP polling page and the tenant's view on
// mijn.norm.eu. Replaces the links to the old reporting app (mijn.norm.eu/client-overview).
import { hasPermission } from '@/logic/common/Permissions'

export const POLLING_PERMISSION = 'ERP:view_polling'

interface AddressParts {
  digits: string
  letters: string
  houseNumber: string
}

// '1234 AB' + '12 a' -> { digits: '1234', letters: 'AB', houseNumber: '12 a' }: the house number as
// the source has it, mijn.norm.eu matches on it as the old reporting links passed it
export function addressParts(postalCode?: string | null, houseNumber?: string | number | null): AddressParts | null {
  const postal = (postalCode || '').replace(/\s+/g, '').toUpperCase()
  const house = String(houseNumber ?? '').trim()
  if (!/^\d{4}[A-Z]{2}$/.test(postal) || !house) return null
  return { digits: postal.slice(0, 4), letters: postal.slice(4), houseNumber: house }
}

// The tickets location id: postcode + house number, the addition after a dash (1234AB12-A).
// Same normalisation as useAddressLocation on the polling page.
export function toLocationId(postalCode?: string | null, houseNumber?: string | number | null): string | null {
  const parts = addressParts(postalCode, houseNumber)
  if (!parts) return null
  return `${parts.digits}${parts.letters}${parts.houseNumber.replace(/\s+/g, '-')}`.toUpperCase()
}

// '1234AB12-A' -> { postalCode: '1234AB', houseNumber: '12-A' }
export function fromLocationId(locationId?: string | null) {
  const match = (locationId || '').toUpperCase().match(/^(\d{4}[A-Z]{2})(\d.*)$/)
  if (!match) return null
  return { postalCode: match[1], houseNumber: match[2] }
}

// The polling page for an address: resolved to one of its meters by the polling-location route.
// A serial number goes straight to the meter.
export function pollingRoute({ locationId, serialNumber }: { locationId?: string | null; serialNumber?: string | null }) {
  if (serialNumber) return { name: 'polling-detail', params: { id: serialNumber } }
  if (locationId) return { name: 'polling-location', params: { locationId } }
  return null
}

// The meter to open for an address, from the tickets location's eleenas: the one that communicated
// last, then the one activated last (a replaced meter stays on the address).
interface LocationEleena {
  serial_number: string
  last_communication_at?: string | null
  activated_at?: string | null
}
export function pickMeter(eleenas: LocationEleena[] = []): string | null {
  const newest = (a?: string | null, b?: string | null) => (b || '').localeCompare(a || '')
  const sorted = [...eleenas].sort(
    (a, b) => newest(a.last_communication_at, b.last_communication_at) || newest(a.activated_at, b.activated_at)
  )
  return sorted[0]?.serial_number ?? null
}

export function canViewPolling() {
  return hasPermission([POLLING_PERMISSION])
}

// mijn.norm.eu (the tenant app) opened on the tenant of the address. Only works for someone logged
// in there as admin: the app resolves the meter from these parameters (getMeterIdAdmin).
export function mijnNormUrl(postalCode?: string | null, houseNumber?: string | number | null): string | null {
  const parts = addressParts(postalCode, houseNumber)
  if (!parts) return null
  const params = new URLSearchParams({
    postcode: parts.digits,
    postcode_ext: parts.letters,
    housenmbr: parts.houseNumber,
  })
  return `https://mijn.norm.eu/mijnmonitor/?${params}`
}

export const MIJN_NORM_TITLE = 'Huurdersweergave op mijn.norm.eu (je moet daar als admin ingelogd zijn)'
export const POLLING_DISABLED_TITLE = `Hiervoor heb je recht ${POLLING_PERMISSION} nodig`
